import type {} from './gtm.global';
/**
 * Configuration for stape.io server-side Google Tag Manager container.
 *
 * Supports user ID tracking from multiple sources (cookie, localStorage,
 * JS variable, CSS selector, or stapeUserId).
 */
export type GoogleTagManagerConfig = {
  serverUrl: string;
  containerId: string;
  userId?: {
    source?: 'cookie' | 'localStorage' | 'jsVariable' | 'cssSelector' | 'stapeUserId';
    key?: string;
    attribute?: string;
  };
}

/**
 * Loads Google Tag Manager via stape.io server-side container.
 *
 * Injects GTM script with stape.io server URL for enhanced privacy and
 * server-side event processing. Supports user ID tracking from various sources.
 *
 * @example
 * ```typescript
 * // Basic setup
 * loadGoogleTagManager({
 *   serverUrl: 'https://your-stape-domain.com',
 *   containerId: 'GTM-XXXXX'
 * });
 *
 * // With user ID tracking
 * loadGoogleTagManager({
 *   serverUrl: 'https://your-stape-domain.com',
 *   containerId: 'GTM-XXXXX',
 *   userId: {
 *     source: 'stapeUserId',
 *     key: 'user_id_key'
 *   }
 * });
 * ```
 */
export const loadGoogleTagManager = (config: GoogleTagManagerConfig): void => {
  if (!config.serverUrl || !config.containerId) {
    return;
  }

  const script = document.createElement('script');
  script.type = 'text/javascript';
  // values
  const serverUrl = config.serverUrl;
  const containerId = config.containerId;
  const dataLayerName = 'dataLayer';

  // User ID configuration
  const userIdConfig = config.userId || {};
  const userIdSource = userIdConfig.source || '';
  const userIdKey = userIdConfig.key || '';
  const userIdAttribute = userIdConfig.attribute || '';

  script.innerHTML = `
    !function(){"use strict";function l(e){for(var t=e,r=0,n=document.cookie.split(";");r<n.length;r++){var o=n[r].split("=");if(o[0].trim()===t)return o[1]}}function s(e){return localStorage.getItem(e)}function u(e){return window[e]}function A(e,t){e=document.querySelector(e);return t?null==e?void 0:e.getAttribute(t):null==e?void 0:e.textContent}var e=window,t=document,r="script",n="${dataLayerName}",o="${serverUrl}",a="",i="${containerId}",c="${userIdKey}",g="${userIdSource}",v="${userIdAttribute}",E="",d=!1;try{var d=!!g&&(m=navigator.userAgent,!!(m=new RegExp("Version/([0-9._]+)(.*Mobile)?.*Safari.*").exec(m)))&&16.4<=parseFloat(m[1]),f="stapeUserId"===g,I=d&&!f?function(e,t,r){void 0===t&&(t="");var n={cookie:l,localStorage:s,jsVariable:u,cssSelector:A},t=Array.isArray(t)?t:[t];if(e&&n[e])for(var o=n[e],a=0,i=t;a<i.length;a++){var c=i[a],c=r?o(c,r):o(c);if(c)return c}else console.warn("invalid uid source",e)}(g,v,E):void 0;d=d&&(!!I||f)}catch(e){console.error(e)}var m=e,g=(m[n]=m[n]||[],m[n].push({"gtm.start":(new Date).getTime(),event:"gtm.js"}),t.getElementsByTagName(r)[0]),v=I?"&bi="+encodeURIComponent(I):"",E=t.createElement(r),f=(d&&(i=8<i.length?i.replace(/([a-z]{8}$)/,"kp$1"):"kp"+i),!d&&a?a:o);E.async=!0,E.src=f+"/"+i+".js?"+c+v,null!=(e=g.parentNode)&&e.insertBefore(E,g)}();
  `;

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];

  // Append the script to the head
  document.head.appendChild(script);
}
